import type { MissionObjective } from "@frontier/content/contracts";
import type { PublicEventsView, PublicEventView } from "@frontier/content/events";
import type { ShipSnapshot, WorldSnapshot } from "@frontier/protocol/contracts";

export function trackedPublicEvent(
  world: WorldSnapshot,
  events: PublicEventsView,
): PublicEventView | undefined {
  return world.publicEvents.find(
    (event) =>
      event.id === events.trackedEventId &&
      (event.status === "available" || event.status === "active"),
  );
}
export function publicEventObjective(
  world: WorldSnapshot,
  pilot: Pick<ShipSnapshot, "x" | "y">,
  event: PublicEventView,
): MissionObjective {
  const region = world.regions.find(
    (item) =>
      pilot.x >= item.x &&
      pilot.x < item.x + item.width &&
      pilot.y >= item.y &&
      pilot.y < item.y + item.height,
  );
  if (!region || region.id === event.sectorId) return event.objective;
  const queue = [{ regionId: region.id, firstGateId: "" }];
  const visited = new Set([region.id]);
  while (queue.length) {
    const next = queue.shift();
    if (!next) break;
    const area = world.regions.find((item) => item.id === next.regionId);
    if (!area) continue;
    const gates = world.gates.filter(
      (gate) =>
        gate.x >= area.x &&
        gate.x < area.x + area.width &&
        gate.y >= area.y &&
        gate.y < area.y + area.height,
    );
    for (const gate of gates) {
      const firstGateId = next.firstGateId || gate.id;
      if (gate.toSectorId === event.sectorId) {
        const first = world.gates.find((item) => item.id === firstGateId);
        if (first)
          return {
            name: first.name,
            x: first.x,
            y: first.y,
            targetId: first.id,
            kind: "travel",
          };
      }
      if (!visited.has(gate.toSectorId)) {
        visited.add(gate.toSectorId);
        queue.push({ regionId: gate.toSectorId, firstGateId });
      }
    }
  }
  return event.objective;
}
export function eventWorkAvailable(
  world: WorldSnapshot,
  pilot: Pick<ShipSnapshot, "x" | "y" | "dead" | "docked">,
  events: PublicEventsView,
): PublicEventView | undefined {
  const event = trackedPublicEvent(world, events);
  return event?.type === "salvage" &&
    event.status === "active" &&
    !pilot.dead &&
    !pilot.docked &&
    Math.hypot(pilot.x - event.x, pilot.y - event.y) <= 110
    ? event
    : undefined;
}

export function eventNavigationCaption(
  event: PublicEventView,
  objective: MissionObjective,
): string {
  if (objective.kind === "travel" && objective.targetId !== event.objective.targetId)
    return objective.name;
  return {
    escort: "Конвой «Орион»",
    raid: "Ретранслятор",
    salvage: "Обломки",
    boss: "Голиаф",
    relay: "Спорный ретранслятор",
    truce: "Нарушитель перемирия",
  }[event.type];
}
